import React, { useEffect, useState } from 'react';
import { SettingsIcon, BuildingIcon, UploadIcon, Trash2Icon, ChevronDownIcon, SaveIcon, CheckCircleIcon } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
type TabType = 'dealer' | 'design' | 'quote';
type DealerSubTabType = 'legal' | 'address' | 'contact';
export function SystemSettingsPage() {
  const {
    role,
    companyName,
    companyLogo,
    setCompanyLogo,
    officialCountry,
    setOfficialCountry
  } = useUser();
  const [activeTab, setActiveTab] = useState<TabType>('dealer');
  const [dealerSubTab, setDealerSubTab] = useState<DealerSubTabType>('legal');
  const [logoPreview, setLogoPreview] = useState<string | null>(companyLogo);
  // Legal data
  const [legalName, setLegalName] = useState('DAIRY GLOBAL EXPERTS LLC LN');
  const [taxId, setTaxId] = useState('INN 392949923233');
  const [billingCountry, setBillingCountry] = useState('');
  // Billing address
  const [lineAddress1, setLineAddress1] = useState('50 Centralna st');
  const [lineAddress2, setLineAddress2] = useState('');
  const [city, setCity] = useState('Hromy village, Uman district');
  const [stateProvince, setStateProvince] = useState('Cherkasy Region');
  const [zipCode, setZipCode] = useState('20343');
  const [distributionCountry, setDistributionCountry] = useState('Ukraine');
  // Contact
  const [phone, setPhone] = useState('+380 97 410 2423');
  const [email, setEmail] = useState('irrigation.dge@gmail.com');
  const [url, setUrl] = useState('www.dge.com.ua');
  const [dealerMarkup, setDealerMarkup] = useState('25.00');
  // Design Preferences - Irrigation Capacity
  const [applicationRate, setApplicationRate] = useState('1.30');
  const [applicationRateUnit, setApplicationRateUnit] = useState('l/s/ha');
  const [irrigationTimingMode, setIrrigationTimingMode] = useState<'hours' | 'percentage'>('hours');
  const [hoursPerDay, setHoursPerDay] = useState('18');
  const [daysPerWeek, setDaysPerWeek] = useState('7');
  const [percentageRate, setPercentageRate] = useState('75.00');
  // Design Preferences - General Configuration
  const [brand, setBrand] = useState('Pierce');
  const [model, setModel] = useState('CP600');
  const [electricalFrequency, setElectricalFrequency] = useState('60 Hz');
  const [profile, setProfile] = useState('Standard');
  // Design Preferences - Controls
  const [communicationType, setCommunicationType] = useState('GSM 4G');
  const [temperatureSensor, setTemperatureSensor] = useState(true);
  const [dosingPumpControl, setDosingPumpControl] = useState(false);
  const [stopInSlot, setStopInSlot] = useState(false);
  const [lightingArrestor, setLightingArrestor] = useState(false);
  // Design Preferences - Pivot Point
  const [pivotProfile, setPivotProfile] = useState('Standard');
  const [pivotProtectiveCoating, setPivotProtectiveCoating] = useState('Galvanized');
  const [standPipeDiameter, setStandPipeDiameter] = useState('8"5/8');
  const [heavyDuty, setHeavyDuty] = useState(false);
  // Design Preferences - Spans
  const [outletSpacing, setOutletSpacing] = useState('1.5 m');
  const [spansProtectiveCoating, setSpansProtectiveCoating] = useState('Galvanized');
  const [spansProfile, setSpansProfile] = useState('Standard');
  // Design Preferences - Drive Train
  const [motorCover, setMotorCover] = useState(true);
  const [centerDrive, setCenterDrive] = useState('UMC 35/2...');
  const [wdGearbox, setWdGearbox] = useState('Std. 52/1');
  const [tires, setTires] = useState('14.9x24');
  // Design Preferences - Electric Cables
  const [cableProtection, setCableProtection] = useState('Unshield');
  const [wireSize, setWireSize] = useState('4 mm²');
  // Design Preferences - Sprinklers
  const [maxSprinklerSpacing, setMaxSprinklerSpacing] = useState('1.50');
  const [sprinklerName, setSprinklerName] = useState('Nelson | Rotator-R3...');
  const [pad, setPad] = useState('Orange');
  const [drops, setDrops] = useState('Flex Drop with plast...');
  const [clearanceHeight, setClearanceHeight] = useState('2.60');
  const [regulator, setRegulator] = useState(true);
  const [regulatorBrand, setRegulatorBrand] = useState('Komet');
  const [nominalPressure, setNominalPressure] = useState('10 psi');
  const [verticalPosition, setVerticalPosition] = useState('Top');
  // Design Preferences - End Gun 1
  const [endGun1Brand, setEndGun1Brand] = useState('Komet');
  const [endGun1Model, setEndGun1Model] = useState('KPW HEAD-UP');
  const [endGun1Control, setEndGun1Control] = useState('Endgun Control wit...');
  // Design Preferences - End Gun 2
  const [endGun2Brand, setEndGun2Brand] = useState('');
  const [endGun2Model, setEndGun2Model] = useState('');
  const [endGun2Control, setEndGun2Control] = useState('');
  // Design Preferences - Booster Pump
  const [boosterPumpPower, setBoosterPumpPower] = useState('');
  // Save status tracking
  const [savingTab, setSavingTab] = useState<string | null>(null);
  const [savedTab, setSavedTab] = useState<string | null>(null);
  // Initialize state
  useEffect(() => {
    setLogoPreview(companyLogo);
  }, [companyLogo]);
  // Determine if user can edit logo and official country
  const canEditLogo = ['sales-manager', 'operations-manager', 'manager-business-unit', 'ceo', 'logistic', 'marketing', 'financial-manager', 'system-admin', 'dealer-admin', 'region-admin'].includes(role);
  const canEditOfficialCountry = canEditLogo;
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleRemoveLogo = () => {
    setLogoPreview(null);
  };
  const handleSaveDealerInfo = () => {
    setSavingTab('dealer');
    setCompanyLogo(logoPreview);
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('dealer');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSaveDesignPreferences = () => {
    setSavingTab('design');
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('design');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSaveQuote = () => {
    setSavingTab('quote');
    setTimeout(() => {
      setSavingTab(null);
      setSavedTab('quote');
      setTimeout(() => setSavedTab(null), 2000);
    }, 600);
  };
  const handleSave = () => {
    setCompanyLogo(logoPreview);
    // In a real app, we would save all the other fields here too
    alert('Settings saved successfully!');
  };
  const countries = ['México', 'Estados Unidos', 'España', 'Colombia', 'Argentina', 'Brasil', 'Chile', 'Perú', 'Alemania', 'Francia', 'Ucrania', 'Ukraine', 'Reino Unido', 'Italia', 'Canadá'];
  const tabs = [{
    id: 'dealer' as TabType,
    label: 'Dealer Information'
  }, {
    id: 'design' as TabType,
    label: 'Design Preferences'
  }, {
    id: 'quote' as TabType,
    label: 'Quote'
  }];
  const dealerSubTabs = [{
    id: 'legal' as DealerSubTabType,
    label: 'Legal Data'
  }, {
    id: 'address' as DealerSubTabType,
    label: 'Billing Address'
  }, {
    id: 'contact' as DealerSubTabType,
    label: 'Contact'
  }];
  return <div className="flex flex-col h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950" data-id="element-12760" data-mp="243pngczsh1xk">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-12761" data-mp="2d1rvvdluqwuv">
        <div className="absolute top-[10%] left-[5%] w-64 h-64 bg-blue-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-12762" data-mp="283zvrwb204fl"></div>
        <div className="absolute top-[40%] right-[10%] w-72 h-72 bg-purple-600 rounded-full mix-blend-multiply filter blur-[80px] opacity-20" data-id="element-12763" data-mp="2mzz8jk93ysr2"></div>
        <div className="absolute bottom-[10%] left-[25%] w-80 h-80 bg-teal-600 rounded-full mix-blend-multiply filter blur-[100px] opacity-20" data-id="element-12764" data-mp="sm7qbwcpr0k0"></div>
      </div>

      {/* Header */}
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 px-6 py-4 relative z-10" data-id="element-12765" data-mp="1uh1tbbsk4qu">
        <div className="flex justify-between items-center" data-id="element-12766" data-mp="6tk5osg1c4x8">
          <div className="flex items-center" data-id="element-12767" data-mp="6rpn3ks8kowb">
            <h1 className="text-xl font-bold text-white flex items-center" data-id="element-12768" data-mp="bkinxxkl3d34">
              <SettingsIcon size={24} className="mr-2 text-blue-400" data-id="element-12769" data-mp="bmmuyods4s7m" />
              System Settings
            </h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white/5 backdrop-blur-sm border-b border-white/10 px-6 relative z-10" data-id="element-12770" data-mp="3f2tv4kwzk7bn">
        <div className="flex" data-id="element-12771" data-mp="3k0c94ppv1cqi">
          {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition-colors relative ${activeTab === tab.id ? 'text-white' : 'text-blue-300 hover:text-white'}`} data-id="element-12772" data-mp="3oyniz0g5nn8q">
              {tab.label}
              {activeTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" data-id="element-12773" data-mp="14bajcsttrxpe" />}
            </button>)}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6 relative z-10" data-id="element-12774" data-mp="2sriufw0h6ny6">
        <div className="max-w-4xl mx-auto space-y-6" data-id="element-12775" data-mp="2or8ogjconxjb">
          {/* Dealer Information Tab */}
          {activeTab === 'dealer' && <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 overflow-hidden" data-id="element-12776" data-mp="2jzgx46lpdns4">
              {/* Company Header Section */}
              <div className="px-6 py-4 border-b border-white/10" data-id="element-12777" data-mp="2a2cgemammkip">
                <div className="flex items-center justify-between gap-4" data-id="element-12778" data-mp="252zkzd5ow54i">
                  {/* Left side: Logo and Company Name */}
                  <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-12779" data-mp="210mhywibulbf">
                    {/* Logo Section */}
                    <div className="relative flex-shrink-0" data-id="element-12780" data-mp="1w3mhr285scs8">
                      {logoPreview ? <div className="w-14 h-14 rounded-lg overflow-hidden bg-white/5 border border-white/10" data-id="element-12781" data-mp="3vfp2efsird7h">
                          <img src={logoPreview} alt="Company logo" className="w-full h-full object-cover" data-id="element-12782" data-mp="3qhni5rjcx7uf" />
                        </div> : <div className="w-14 h-14 rounded-lg bg-blue-600/20 flex items-center justify-center border border-white/10" data-id="element-12783" data-mp="6ro76c27kduk">
                          <BuildingIcon size={28} className="text-blue-400" data-id="element-12784" data-mp="ftxfcva07v4s" />
                        </div>}

                      {/* Logo Upload/Remove Controls */}
                      {canEditLogo && <div className="absolute -bottom-1 -right-1 flex gap-1" data-id="element-12785" data-mp="asq76v3y8txl">
                          <label className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-12786" data-mp="3jb5z5koal525">
                            <UploadIcon size={10} data-id="element-12787" data-mp="3t1hb9ui4qkk4" />
                            <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-id="element-12788" data-mp="3t2acyh28ejve" />
                          </label>
                          {logoPreview && <button onClick={handleRemoveLogo} className="bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow-lg border-2 border-[#0f1d3a] transition-colors" data-id="element-12789" data-mp="30ittanyfjln9">
                              <Trash2Icon size={10} data-id="element-12790" data-mp="1b1yaax1lhdxn" />
                            </button>}
                        </div>}
                    </div>

                    {/* Company Name */}
                    <div className="flex-1 min-w-0" data-id="element-12791" data-mp="123488r5hvvcp">
                      <h3 className="text-lg font-semibold text-white truncate" data-id="element-12792" data-mp="1208bv79apeqd">
                        {companyName}
                      </h3>
                    </div>
                  </div>

                  {/* Right side: Distribution Country */}
                  <div className="flex-shrink-0" data-id="element-12793" data-mp="1yrk9hkhyvt5n">
                    <p className="text-xs text-blue-300 mb-1" data-id="element-12794" data-mp="1yq95bjkm0je0">
                      Distribuidor oficial en
                    </p>
                    {canEditOfficialCountry ? <div className="relative" data-id="element-12795" data-mp="1pqm2ctc8f5iu">
                        <select value={distributionCountry} onChange={e => setDistributionCountry(e.target.value)} className="appearance-none bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 pr-8 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12796" data-mp="9dnki6stl2mt">
                          {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-12797" data-mp="3mfhk94yu3ats">
                              {c}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2 text-blue-300" data-id="element-12798" data-mp="3cpetlbf6q8ym">
                          <ChevronDownIcon size={14} data-id="element-12799" data-mp="37r3iyyh2fufc" />
                        </div>
                      </div> : <p className="text-sm font-medium text-white" data-id="element-12800" data-mp="32po11fa6q416">
                        {distributionCountry || 'No country set'}
                      </p>}
                  </div>
                </div>
              </div>

              {/* Sub Tabs */}
              <div className="flex border-b border-white/10 px-6" data-id="element-12801" data-mp="wx7hg71wo65h">
                {dealerSubTabs.map(tab => <button key={tab.id} onClick={() => setDealerSubTab(tab.id)} className={`px-4 py-3 text-sm font-medium transition-colors relative ${dealerSubTab === tab.id ? 'text-white' : 'text-blue-300 hover:text-white'}`} data-id="element-12802" data-mp="ryeakxh4jdea">
                    {tab.label}
                    {dealerSubTab === tab.id && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500" data-id="element-12803" data-mp="14enkkm3pnmn2" />}
                  </button>)}
              </div>

              {/* Sub Tab Content */}
              <div className="p-6" data-id="element-12804" data-mp="1e60851ih7qna">
                {/* Legal Data Sub Tab */}
                {dealerSubTab === 'legal' && <div className="space-y-5" data-id="element-12805" data-mp="gl5opzbkou2c">
                    <div data-id="element-12806" data-mp="lhcjqx2fkl5i">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12807" data-mp="linytt1btfbx">
                        Legal name <span className="text-red-400" data-id="element-12808" data-mp="qfntl7sq6vnq">*</span>
                      </label>
                      <input type="text" value={legalName} onChange={e => setLegalName(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Company legal name" data-id="element-12809" data-mp="ujlrjf6be0f7" />
                    </div>

                    <div data-id="element-12810" data-mp="1zi8x0wqrljjw">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12811" data-mp="1zj0ukd3x9rct">
                        TAX ID / VAT ID / NIF{' '}
                        <span className="text-red-400" data-id="element-12812" data-mp="2ecn90dbwvsp2">*</span>
                      </label>
                      <input type="text" value={taxId} onChange={e => setTaxId(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Tax identification number" data-id="element-12813" data-mp="29hzvf9ig4ip9" />
                    </div>
                  </div>}

                {/* Address Sub Tab */}
                {dealerSubTab === 'address' && <div className="space-y-5" data-id="element-12814" data-mp="3677f0bzi8hqj">
                    <div data-id="element-12815" data-mp="363tc92mxi6ik">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12816" data-mp="325435m8xbb4q">
                        Address line 1 <span className="text-red-400" data-id="element-12817" data-mp="3ksczeqju6pwq">*</span>
                      </label>
                      <input type="text" value={lineAddress1} onChange={e => setLineAddress1(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Street, number" data-id="element-12818" data-mp="3prpm9bapjwzb" />
                    </div>

                    <div data-id="element-12819" data-mp="320af957zkrhq">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12820" data-mp="36strnyuxtgky">
                        Address line 2
                      </label>
                      <input type="text" value={lineAddress2} onChange={e => setLineAddress2(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Apartment, suite, etc. (optional)" data-id="element-12821" data-mp="2t3ji0php76lk" />
                    </div>

                    <div data-id="element-12822" data-mp="tnygr4p21cf6">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12823" data-mp="to88awpqzrmz">
                        City <span className="text-red-400" data-id="element-12824" data-mp="otkhusb6on5r">*</span>
                      </label>
                      <input type="text" value={city} onChange={e => setCity(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="City" data-id="element-12825" data-mp="krz8hgxuyr0b" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-12826" data-mp="13k1s915sqnes">
                      <div data-id="element-12827" data-mp="ylgsf2ugi5qv">
                        <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12828" data-mp="ym9ptd7iziwc">
                          State / Province
                        </label>
                        <input type="text" value={stateProvince} onChange={e => setStateProvince(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="State or province" data-id="element-12829" data-mp="3o8fucov2tr48" />
                      </div>

                      <div data-id="element-12830" data-mp="3j4nev7rpu37b">
                        <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12831" data-mp="3nwomuhhup7rj">
                          Zip code
                        </label>
                        <input type="text" value={zipCode} onChange={e => setZipCode(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="Zip code" data-id="element-12832" data-mp="3rxqmgzfojlu1" />
                      </div>
                    </div>

                    <div data-id="element-12833" data-mp="27jgl8p3zlty9">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12834" data-mp="27k9hux98f32o">
                        Billing country <span className="text-red-400" data-id="element-12835" data-mp="2cjm69mfr4i7d">*</span>
                      </label>
                      <div className="relative" data-id="element-12836" data-mp="i7thcxgxoh93">
                        <select value={billingCountry} onChange={e => setBillingCountry(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12837" data-mp="db31aa54plno">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-12838" data-mp="3vfo4oipnyr8s">
                            Please, select country
                          </option>
                          {countries.map(c => <option key={c} value={c} className="bg-slate-800 text-white" data-id="element-12839" data-mp="8b8ccc4wz659">
                              {c}
                            </option>)}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12840" data-mp="1jqdd3alhium8">
                          <ChevronDownIcon size={16} data-id="element-12841" data-mp="1fo08wqmdz4rk" />
                        </div>
                      </div>
                      {!billingCountry && <p className="text-red-400 text-xs mt-1" data-id="element-12842" data-mp="1n79hwiqzd1g3">
                          Please, select country
                        </p>}
                    </div>
                  </div>}

                {/* Contact Sub Tab */}
                {dealerSubTab === 'contact' && <div className="space-y-5" data-id="element-12843" data-mp="1i8oiumngsnt8">
                    <div data-id="element-12844" data-mp="1ibkge9vehah5">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12845" data-mp="25zc3m8ihb7x7">
                        Phone
                      </label>
                      <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="+1 234 567 8900" data-id="element-12846" data-mp="2axlzklc12cjv" />
                    </div>

                    <div data-id="element-12847" data-mp="2tod6lg8jlqv3">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12848" data-mp="2tpojc5jyu8yc">
                        Email
                      </label>
                      <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="company@email.com" data-id="element-12849" data-mp="p6phxf1gsszq" />
                    </div>

                    <div data-id="element-12850" data-mp="6d2ex1cnx02p">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12851" data-mp="6fycgokllmqm">
                        Website
                      </label>
                      <input type="url" value={url} onChange={e => setUrl(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="www.company.com" data-id="element-12852" data-mp="3slva5fyyc1q9" />
                    </div>
                  </div>}
              </div>

              {/* Save Button for Dealer Information (common for all 3 sub-tabs) */}
              <div className="px-6 py-4 border-t border-white/10 flex items-center justify-end bg-black/10 rounded-b-xl" data-id="element-12853" data-mp="3bbr9u5298f9f">
                <button onClick={handleSaveDealerInfo} disabled={savingTab === 'dealer'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-12854" data-mp="3q0vv5v4cizlr">
                  {savingTab === 'dealer' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-12855" data-mp="3u1phik2546cj" />
                      Saving...
                    </> : savedTab === 'dealer' ? <>
                      <CheckCircleIcon size={16} data-id="element-12856" data-mp="7vkc0f6itc8k" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-12857" data-mp="2elqo6ex6mewc" />
                      Save Dealer Information
                    </>}
                </button>
              </div>
            </div>}

          {/* Design Preferences Tab */}
          {activeTab === 'design' && <div className="space-y-6" data-id="element-12858" data-mp="33bv4wsrod1dm">
              {/* Irrigation Capacity Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12859" data-mp="38bhf0tc7i1i7">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12860" data-mp="3db3t14zuzls2">
                  Irrigation Capacity
                </h3>
                <div className="space-y-5" data-id="element-12861" data-mp="3maqx5yjz35ot">
                  <div data-id="element-12862" data-mp="3m9frft6yvnv2">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12863" data-mp="3m8w7k6xrapee">
                      Application rate
                    </label>
                    <div className="flex gap-3" data-id="element-12864" data-mp="3vzquzemqnv1f">
                      <input type="number" step="0.01" value={applicationRate} onChange={e => setApplicationRate(e.target.value)} className="w-32 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12865" data-mp="3w2mv99m26vt1" />
                      <div className="relative flex-1" data-id="element-12866" data-mp="1pc0a3imorrqz">
                        <select value={applicationRateUnit} onChange={e => setApplicationRateUnit(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12867" data-mp="1pemhni9ku797">
                          <option value="l/s/ha" className="bg-slate-800 text-white" data-id="element-12868" data-mp="2d3ymy71s9v68">
                            l/s/ha
                          </option>
                          <option value="mm/h" className="bg-slate-800 text-white" data-id="element-12869" data-mp="23b00aupukep9">
                            mm/h
                          </option>
                          <option value="gpm/ac" className="bg-slate-800 text-white" data-id="element-12870" data-mp="2vsltktzlyslx">
                            gpm/ac
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12871" data-mp="2r02f7ut3jtg2">
                          <ChevronDownIcon size={16} data-id="element-12872" data-mp="2gji1zma7z1w0" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12873" data-mp="21nspceqxc13y">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12874" data-mp="26ksrwfokelqb">
                      Irrigation timing rate
                    </label>
                    <div className="space-y-3" data-id="element-12875" data-mp="2uavhmp50bflr">
                      <div className="flex items-center gap-3" data-id="element-12876" data-mp="2z96rgzvaxq3z">
                        <input type="radio" id="timing-hours" checked={irrigationTimingMode === 'hours'} onChange={() => setIrrigationTimingMode('hours')} className="w-4 h-4 text-blue-500 accent-blue-500" data-id="element-12877" data-mp="2zbj7h7hi1qee" />
                        <div className="flex items-center gap-2 flex-1" data-id="element-12878" data-mp="2phaigtrogfkz">
                          <input type="number" value={hoursPerDay} onChange={e => setHoursPerDay(e.target.value)} disabled={irrigationTimingMode !== 'hours'} className="w-20 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12879" data-mp="3d7oaika9iefk" />
                          <span className="text-white text-sm" data-id="element-12880" data-mp="1gba9otprnx0l">h/day</span>
                          <input type="number" value={daysPerWeek} onChange={e => setDaysPerWeek(e.target.value)} disabled={irrigationTimingMode !== 'hours'} className="w-20 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12881" data-mp="1gclfez2rveuc" />
                          <span className="text-white text-sm" data-id="element-12882" data-mp="wnkpb7k5sz1e">days/week</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-3" data-id="element-12883" data-mp="snagzo8w9wjd">
                        <input type="radio" id="timing-percentage" checked={irrigationTimingMode === 'percentage'} onChange={() => setIrrigationTimingMode('percentage')} className="w-4 h-4 text-blue-500 accent-blue-500" data-id="element-12884" data-mp="280n3xx2p7eix" />
                        <div className="flex items-center gap-2 flex-1" data-id="element-12885" data-mp="10p772lll52tf">
                          <input type="number" step="0.01" value={percentageRate} onChange={e => setPercentageRate(e.target.value)} disabled={irrigationTimingMode !== 'percentage'} className="w-24 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12886" data-mp="15n8s3c2knc7j" />
                          <span className="text-white text-sm" data-id="element-12887" data-mp="mx97p2hoelag">% 18h/24h</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* General Configuration Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12888" data-mp="3vdu8hw05m82h">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12889" data-mp="3906y422h6zc">
                  General Configuration
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5" data-id="element-12890" data-mp="3mx3eqpug1lq7">
                  <div data-id="element-12891" data-mp="3iv8dtmgfdacy">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12892" data-mp="3iuqh6opuou4i">
                      Brand
                    </label>
                    <div className="relative" data-id="element-12893" data-mp="3dvvs551ai9by">
                      <select value={brand} onChange={e => setBrand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12894" data-mp="38xlwktbnl6pt">
                        <option value="Pierce" className="bg-slate-800 text-white" data-id="element-12895" data-mp="2v5nuh2gd0b35">
                          Pierce
                        </option>
                        <option value="Reinke" className="bg-slate-800 text-white" data-id="element-12896" data-mp="2q59kvfz40t30">
                          Reinke
                        </option>
                        <option value="Valley" className="bg-slate-800 text-white" data-id="element-12897" data-mp="cys35ut3orzl">
                          Valley
                        </option>
                        <option value="Zimmatic" className="bg-slate-800 text-white" data-id="element-12898" data-mp="hwlbatdyrw2i">
                          Zimmatic
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12899" data-mp="8w57fitb6mx4">
                        <ChevronDownIcon size={16} data-id="element-12900" data-mp="3qc511iffewct" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12901" data-mp="3v699650fhafh">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12902" data-mp="3hdjj9phs3ld3">
                      Model
                    </label>
                    <div className="relative" data-id="element-12903" data-mp="3lf4p4qvwazds">
                      <select value={model} onChange={e => setModel(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12904" data-mp="37f3tpqyegk5p">
                        <option value="CP600" className="bg-slate-800 text-white" data-id="element-12905" data-mp="2sty8xba5o9yz">
                          CP600
                        </option>
                        <option value="CP900" className="bg-slate-800 text-white" data-id="element-12906" data-mp="32rcciaazd49v">
                          CP900
                        </option>
                        <option value="CP1200" className="bg-slate-800 text-white" data-id="element-12907" data-mp="2xtkm5kpzhq7s">
                          CP1200
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12908" data-mp="36rmlzwxzecth">
                        <ChevronDownIcon size={16} data-id="element-12909" data-mp="3ghplmfzlyp0s" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12910" data-mp="3ugpbts2rrbz2">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12911" data-mp="3uia2v7pvkcs6">
                      Electrical Frequency
                    </label>
                    <div className="relative" data-id="element-12912" data-mp="voxwf6nuwa7d">
                      <select value={electricalFrequency} onChange={e => setElectricalFrequency(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12913" data-mp="voo7lmejs939">
                        <option value="60 Hz" className="bg-slate-800 text-white" data-id="element-12914" data-mp="hp5b5qv3mm6s">
                          60 Hz
                        </option>
                        <option value="50 Hz" className="bg-slate-800 text-white" data-id="element-12915" data-mp="7z3jkp3jeln4">
                          50 Hz
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12916" data-mp="3u6trt6vmk501">
                        <ChevronDownIcon size={16} data-id="element-12917" data-mp="3q5qdwpx50j1g" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12918" data-mp="3l7ghyd3l9ees">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12919" data-mp="3l88atg5swy1d">
                      Profile
                    </label>
                    <div className="relative" data-id="element-12920" data-mp="25mehcm75luev">
                      <select value={profile} onChange={e => setProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12921" data-mp="25pafaaj04j3b">
                        <option value="Standard" className="bg-slate-800 text-white" data-id="element-12922" data-mp="1hz6e09tacvfh">
                          Standard
                        </option>
                        <option value="Heavy" className="bg-slate-800 text-white" data-id="element-12923" data-mp="1qxrqpoc2tg8c">
                          Heavy
                        </option>
                        <option value="Extra Heavy" className="bg-slate-800 text-white" data-id="element-12924" data-mp="1vqky6jyxj2o4">
                          Extra Heavy
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12925" data-mp="1374guxilogj5">
                        <ChevronDownIcon size={16} data-id="element-12926" data-mp="26hi9fdqxvpzt" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12927" data-mp="2fdr8grb08mlf">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12928" data-mp="1njz0sb8e89dx">
                  Controls
                </h3>
                <div className="space-y-5" data-id="element-12929" data-mp="1inqlmxu74g6r">
                  <div data-id="element-12930" data-mp="1wlp0038iocm9">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12931" data-mp="1wn04645vjmdw">
                      Communication type
                    </label>
                    <div className="relative" data-id="element-12932" data-mp="380c3c1qovgtq">
                      <select value={communicationType} onChange={e => setCommunicationType(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12933" data-mp="37z0x7v9rtwzg">
                        <option value="GSM 4G" className="bg-slate-800 text-white" data-id="element-12934" data-mp="3flfvp3qwoxk8">
                          GSM 4G
                        </option>
                        <option value="GSM 3G" className="bg-slate-800 text-white" data-id="element-12935" data-mp="36iwu0myuwqzk">
                          GSM 3G
                        </option>
                        <option value="Radio" className="bg-slate-800 text-white" data-id="element-12936" data-mp="3u9stm69k0qhd">
                          Radio
                        </option>
                        <option value="WiFi" className="bg-slate-800 text-white" data-id="element-12937" data-mp="3kjps1hoda47f">
                          WiFi
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12938" data-mp="h2hcx5l0xige">
                        <ChevronDownIcon size={16} data-id="element-12939" data-mp="ltpp29dqnhx9" />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-12940" data-mp="2tdk5cc9u9f86">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-12941" data-mp="2paxlaxjesylj">
                      <input type="checkbox" checked={temperatureSensor} onChange={e => setTemperatureSensor(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-12942" data-mp="2kbuf39rp0mbz" />
                      <span className="text-white text-sm" data-id="element-12943" data-mp="2ff3xgi08piog">
                        Temperature Sensor
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-12944" data-mp="382q6i5s8lfip">
                      <input type="checkbox" checked={dosingPumpControl} onChange={e => setDosingPumpControl(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-12945" data-mp="33a6pszy9649o" />
                      <span className="text-white text-sm" data-id="element-12946" data-mp="3vr9cgentdg7m">
                        Dosing Pump Control
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-12947" data-mp="3qyyehhcmw4hp">
                      <input type="checkbox" checked={stopInSlot} onChange={e => setStopInSlot(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-12948" data-mp="27cq63y5qxwc" />
                      <span className="text-white text-sm" data-id="element-12949" data-mp="c4h97uwpid8x">
                        Stop in slot on Pivot Point
                      </span>
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-12950" data-mp="h4vcz0r9z115">
                      <input type="checkbox" checked={lightingArrestor} onChange={e => setLightingArrestor(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-12951" data-mp="l4mdvxaimxje" />
                      <span className="text-white text-sm" data-id="element-12952" data-mp="uup2zmeinrcg">
                        Lighting arrestor
                      </span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Pivot Point Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12953" data-mp="14rbqyk252yip">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12954" data-mp="35wfj11tltxlz">
                  Pivot Point
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-12955" data-mp="30ugf9qtycyoi">
                  <div data-id="element-12956" data-mp="3ep93rertlp78">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12957" data-mp="3epiuj4kow0dz">
                      Profile
                    </label>
                    <div className="relative" data-id="element-12958" data-mp="3oksulmrj1xp4">
                      <select value={pivotProfile} onChange={e => setPivotProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12959" data-mp="3onotbdb78qem">
                        <option value="Standard" className="bg-slate-800 text-white" data-id="element-12960" data-mp="3tiuc439b1oyh">
                          Standard
                        </option>
                        <option value="Heavy" className="bg-slate-800 text-white" data-id="element-12961" data-mp="1z7z5h6hokjyc">
                          Heavy
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12962" data-mp="1t4cbz21zw9lb">
                        <ChevronDownIcon size={16} data-id="element-12963" data-mp="1fc4mlvd1jkwn" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12964" data-mp="1kafwg63c5vev">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12965" data-mp="15ezw5jc5x3a1">
                      Protective Coating
                    </label>
                    <div className="relative" data-id="element-12966" data-mp="1aew3nsk2jcuc">
                      <select value={pivotProtectiveCoating} onChange={e => setPivotProtectiveCoating(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12967" data-mp="webjot2f3az9">
                        <option value="Galvanized" className="bg-slate-800 text-white" data-id="element-12968" data-mp="2vrz54dzoqqkt">
                          Galvanized
                        </option>
                        <option value="Painted" className="bg-slate-800 text-white" data-id="element-12969" data-mp="88l785toyh1e">
                          Painted
                        </option>
                        <option value="Stainless" className="bg-slate-800 text-white" data-id="element-12970" data-mp="3ul9xkfc3lnzk">
                          Stainless
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12971" data-mp="3llwnbkg5gvds">
                        <ChevronDownIcon size={16} data-id="element-12972" data-mp="3gl8wozvu2gk3" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-12973" data-mp="3bnh7idmkp8jl">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12974" data-mp="37llwuiir5it5">
                      Stand Pipe Diameter
                    </label>
                    <div className="relative" data-id="element-12975" data-mp="19vatigy6bz28">
                      <select value={standPipeDiameter} onChange={e => setStandPipeDiameter(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12976" data-mp="15uijvmle3s5f">
                        <option value='8"5/8' className="bg-slate-800 text-white" data-id="element-12977" data-mp="2xhxex6mcebis">
                          8"5/8
                        </option>
                        <option value='6"5/8' className="bg-slate-800 text-white" data-id="element-12978" data-mp="31ir3813p5sc7">
                          6"5/8
                        </option>
                        <option value="10" className="bg-slate-800 text-white" data-id="element-12979" data-mp="29rta4vxf45q7">
                          10
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12980" data-mp="2dvsp3a31uwhg">
                        <ChevronDownIcon size={16} data-id="element-12981" data-mp="3p93hrutg6nbr" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5" data-id="element-12982" data-mp="3u6d2wy9t0cts">
                  <label className="flex items-center gap-2 cursor-pointer" data-id="element-12983" data-mp="3rkl5j4l5660s">
                    <input type="checkbox" checked={heavyDuty} onChange={e => setHeavyDuty(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-12984" data-mp="3rmo5agc3jdip" />
                    <span className="text-white text-sm" data-id="element-12985" data-mp="4eu5wo7qmv1e">Heavy duty</span>
                  </label>
                </div>
              </div>

              {/* Spans and Drive Train - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-12986" data-mp="ed06n2ht5v4o">
                {/* Spans Section */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-12987" data-mp="jbuzkx9hp02i">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-12988" data-mp="o68ocgtnc8y7">
                    Spans
                  </h3>
                  <div className="space-y-5" data-id="element-12989" data-mp="x5m1pkkw5ho7">
                    <div data-id="element-12990" data-mp="x2q09m9u4av0">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-12991" data-mp="1234b1c2tlywq">
                        Outlet Spacing
                      </label>
                      <div className="relative" data-id="element-12992" data-mp="1c5hpyqdm02h1">
                        <select value={outletSpacing} onChange={e => setOutletSpacing(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-12993" data-mp="17880xbu4tstq">
                          <option value="1.5 m" className="bg-slate-800 text-white" data-id="element-12994" data-mp="1uybv6slfkg82">
                            1.5 m
                          </option>
                          <option value="3 m" className="bg-slate-800 text-white" data-id="element-12995" data-mp="1q125dbu4q2jp">
                            3 m
                          </option>
                          <option value="1.14 m" className="bg-slate-800 text-white" data-id="element-12996" data-mp="1h5m6uhifiz7s">
                            1.14 m
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-12997" data-mp="28xbls5si756v">
                          <ChevronDownIcon size={16} data-id="element-12998" data-mp="71m4l7bjzkau" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-12999" data-mp="c18htgrdt0jn">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13000" data-mp="3tgfawx54vgpn">
                        Protective Coating
                      </label>
                      <div className="relative" data-id="element-13001" data-mp="27hwulugr88p">
                        <select value={spansProtectiveCoating} onChange={e => setSpansProtectiveCoating(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13002" data-mp="3kexvlgzjvj20">
                          <option value="Galvanized" className="bg-slate-800 text-white" data-id="element-13003" data-mp="3fgw54azsohgi">
                            Galvanized
                          </option>
                          <option value="Painted" className="bg-slate-800 text-white" data-id="element-13004" data-mp="1dohrvp87416f">
                            Painted
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13005" data-mp="2t7z4688wsygx">
                          <ChevronDownIcon size={16} data-id="element-13006" data-mp="37wvcm6evodi2" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13007" data-mp="334kaqy0kuhmv">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13008" data-mp="3gx9v6y0gjehv">
                        Profile
                      </label>
                      <div className="relative" data-id="element-13009" data-mp="3cuwskih0bwpb">
                        <select value={spansProfile} onChange={e => setSpansProfile(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13010" data-mp="3qvfur8ysk8jk">
                          <option value="Standard" className="bg-slate-800 text-white" data-id="element-13011" data-mp="1ribureqdtzgs">
                            Standard
                          </option>
                          <option value="Heavy" className="bg-slate-800 text-white" data-id="element-13012" data-mp="1syzcruvnhdqh">
                            Heavy
                          </option>
                          <option value="CN+" className="bg-slate-800 text-white" data-id="element-13013" data-mp="22x5eochgijvc">
                            CN+
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13014" data-mp="1a9187or2a2rl">
                          <ChevronDownIcon size={16} data-id="element-13015" data-mp="1f7b3dzcsd3d7" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Drive Train Section */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13016" data-mp="1oyp8qnrf4xe9">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-13017" data-mp="whmn9cdlfrhw">
                    Drive Train
                  </h3>
                  <div className="space-y-5" data-id="element-13018" data-mp="39rcnv1rhri3d">
                    <div data-id="element-13019" data-mp="35qkd242z154z">
                      <label className="flex items-center gap-2 cursor-pointer mb-4" data-id="element-13020" data-mp="35q13banh5n21">
                        <input type="checkbox" checked={motorCover} onChange={e => setMotorCover(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-13021" data-mp="30qoievg5yq23" />
                        <span className="text-white text-sm" data-id="element-13022" data-mp="2vu7ofkma9hh3">Motor cover</span>
                      </label>
                    </div>

                    <div data-id="element-13023" data-mp="2m1s1e8w4q2pc">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13024" data-mp="2m1ic6niwrzkp">
                        Center Drive
                      </label>
                      <div className="relative" data-id="element-13025" data-mp="2d1lqmurtfgtv">
                        <select value={centerDrive} onChange={e => setCenterDrive(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13026" data-mp="2d36oat9fffvy">
                          <option value="UMC 35/2..." className="bg-slate-800 text-white" data-id="element-13027" data-mp="1tf26360gz8ac">
                            UMC 35/2...
                          </option>
                          <option value="UMC 40/2" className="bg-slate-800 text-white" data-id="element-13028" data-mp="1kfneu4chatos">
                            UMC 40/2
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13029" data-mp="2c6l7x9ircgas">
                          <ChevronDownIcon size={16} data-id="element-13030" data-mp="2h4cwbtk71ka8" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13031" data-mp="23bx2njd6mylz">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13032" data-mp="27fl73t6ipe30">
                        WD Gearbox
                      </label>
                      <div className="relative" data-id="element-13033" data-mp="2v3mf90zsashw">
                        <select value={wdGearbox} onChange={e => setWdGearbox(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13034" data-mp="2zvnnmbttzz2n">
                          <option value="Std. 52/1" className="bg-slate-800 text-white" data-id="element-13035" data-mp="y6qdw1llfap7">
                            Std. 52/1
                          </option>
                          <option value="Std. 40/1" className="bg-slate-800 text-white" data-id="element-13036" data-mp="t85c3xqp0iyn">
                            Std. 40/1
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13037" data-mp="kj1i5j1se2a">
                          <ChevronDownIcon size={16} data-id="element-13038" data-mp="fdvzb8rvtkli" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13039" data-mp="1quma757930lg">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13040" data-mp="1qu2u7u15uma2">
                        Tires
                      </label>
                      <div className="relative" data-id="element-13041" data-mp="3ff1greslor1c">
                        <select value={tires} onChange={e => setTires(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13042" data-mp="3fgcpzu4teqzu">
                          <option value="14.9x24" className="bg-slate-800 text-white" data-id="element-13043" data-mp="3u4zcqgwrul13">
                            14.9x24
                          </option>
                          <option value="11.2x24" className="bg-slate-800 text-white" data-id="element-13044" data-mp="6z82l6lqrrng">
                            11.2x24
                          </option>
                          <option value="11.2x38" className="bg-slate-800 text-white" data-id="element-13045" data-mp="byug7h5hf9ws">
                            11.2x38
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13046" data-mp="2dqzfxet83bi2">
                          <ChevronDownIcon size={16} data-id="element-13047" data-mp="2ip0vvqvcqupb" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Electric Cables Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13048" data-mp="21ex2kzx2o8hz">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-13049" data-mp="1whws8ct6wjl2">
                  Electric Cables
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-13050" data-mp="2p4honroivlvh">
                  <div data-id="element-13051" data-mp="2k5xy1dyzhlws">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13052" data-mp="2k5ep2mrba7uw">
                      Electric cable protection
                    </label>
                    <div className="relative" data-id="element-13053" data-mp="2f97j4tyx0wcy">
                      <select value={cableProtection} onChange={e => setCableProtection(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13054" data-mp="18ru60dbyi7ud">
                        <option value="Unshield" className="bg-slate-800 text-white" data-id="element-13055" data-mp="3t7e849t361en">
                          Unshield
                        </option>
                        <option value="Shield" className="bg-slate-800 text-white" data-id="element-13056" data-mp="brob4gmiqxdm">
                          Shield
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13057" data-mp="koov8b2k29b9">
                        <ChevronDownIcon size={16} data-id="element-13058" data-mp="uopejx0fcpfg" />
                      </div>
                    </div>
                  </div>

                  <div data-id="element-13059" data-mp="ppcsvflahoeg">
                    <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13060" data-mp="pqxlgzo1mx9o">
                      Wire size
                    </label>
                    <div className="relative" data-id="element-13061" data-mp="2rgzf73u8kv4l">
                      <select value={wireSize} onChange={e => setWireSize(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13062" data-mp="2riapznm3n357">
                        <option value="4 mm²" className="bg-slate-800 text-white" data-id="element-13063" data-mp="x83zrugjicn5">
                          4 mm²
                        </option>
                        <option value="6 mm²" className="bg-slate-800 text-white" data-id="element-13064" data-mp="125vokflw1in4">
                          6 mm²
                        </option>
                        <option value="10 mm²" className="bg-slate-800 text-white" data-id="element-13065" data-mp="sb3rre04qzte">
                          10 mm²
                        </option>
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13066" data-mp="j8uhmp8rx8gj">
                        <ChevronDownIcon size={16} data-id="element-13067" data-mp="28pbzw3k5l6li" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sprinklers Section */}
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13068" data-mp="2c6zvm98awmzb">
                <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-13069" data-mp="22826p56yqrfs">
                  Sprinklers
                </h3>

                {/* Sprinkler Preferences Subsection */}
                <div className="mb-6" data-id="element-13070" data-mp="2uwg7fspfs1v1">
                  <h4 className="text-sm font-medium text-blue-200 mb-4" data-id="element-13071" data-mp="2uvejinnbnkvw">
                    Sprinkler Preferences
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-13072" data-mp="2pzz802cse12n">
                    <div data-id="element-13073" data-mp="2l4tkiz3qkecg">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13074" data-mp="2g836ehbhrsto">
                        Max Sprinkler Spacing
                      </label>
                      <div className="flex gap-2" data-id="element-13075" data-mp="3doii8nfecnxd">
                        <input type="number" step="0.01" value={maxSprinklerSpacing} onChange={e => setMaxSprinklerSpacing(e.target.value)} className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="1.50" data-id="element-13076" data-mp="38w8sb7ea7nv3" />
                        <span className="flex items-center text-blue-300 text-sm px-2" data-id="element-13077" data-mp="3q85wjpu7jgg8">
                          m
                        </span>
                      </div>
                    </div>

                    <div data-id="element-13078" data-mp="3ws81alor76j">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13079" data-mp="7zokejkmu721">
                        Name
                      </label>
                      <div className="relative" data-id="element-13080" data-mp="crorcvz4e084">
                        <select value={sprinklerName} onChange={e => setSprinklerName(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13081" data-mp="hqj7fq5pdaoj">
                          <option value="Nelson | Rotator-R3..." className="bg-slate-800 text-white" data-id="element-13082" data-mp="1of1416rtyyto">
                            Nelson | Rotator-R3...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13083" data-mp="1y4ccqsat21h1">
                          <ChevronDownIcon size={16} data-id="element-13084" data-mp="1t72ovh32dxvb" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13085" data-mp="10hvfto5zhpvt">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13086" data-mp="10ievszc2q477">
                        Pad
                      </label>
                      <div className="relative" data-id="element-13087" data-mp="1efaab7b3gnun">
                        <select value={pad} onChange={e => setPad(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13088" data-mp="1afjl3817ufs8">
                          <option value="Orange" className="bg-slate-800 text-white" data-id="element-13089" data-mp="2teqz8sxvaecp">
                            Orange
                          </option>
                          <option value="Blue" className="bg-slate-800 text-white" data-id="element-13090" data-mp="2yevi2pm8zl63">
                            Blue
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13091" data-mp="2og61r1a028ry">
                          <ChevronDownIcon size={16} data-id="element-13092" data-mp="3c71wa65ubi2w" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13093" data-mp="32fp7xoj93a1m">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13094" data-mp="32ee27j68vs7v">
                        Drops
                      </label>
                      <div className="relative" data-id="element-13095" data-mp="3v3td88xwtl3s">
                        <select value={drops} onChange={e => setDrops(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13096" data-mp="3v31hmy4bbndi">
                          <option value="Flex Drop with plast..." className="bg-slate-800 text-white" data-id="element-13097" data-mp="8lt3inzq9kbr">
                            Flex Drop with plast...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13098" data-mp="3vpz8leqthcda">
                          <ChevronDownIcon size={16} data-id="element-13099" data-mp="3qtqwka7x1zac" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13100" data-mp="3h1ct9d16wkm2">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13101" data-mp="3h2nv37b2riaj">
                        Clearance height
                      </label>
                      <div className="flex gap-2" data-id="element-13102" data-mp="380wpe2gjbbgo">
                        <input type="number" step="0.01" value={clearanceHeight} onChange={e => setClearanceHeight(e.target.value)} className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13103" data-mp="380n1ckf1vedm" />
                        <span className="flex items-center text-blue-300 text-sm px-2" data-id="element-13104" data-mp="1si8leoufhbqf">
                          m
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-white/10 my-6" data-id="element-13105" data-mp="zrgh1437lgs0"></div>

                {/* Regulator Subsection */}
                <div data-id="element-13106" data-mp="14q9tquao93r4">
                  <label className="flex items-center gap-2 cursor-pointer mb-4" data-id="element-13107" data-mp="18tpqrrqnl64c">
                    <input type="checkbox" checked={regulator} onChange={e => setRegulator(e.target.checked)} className="w-4 h-4 rounded accent-blue-500" data-id="element-13108" data-mp="1dky24tbjn1k5" />
                    <h4 className="text-sm font-medium text-blue-200" data-id="element-13109" data-mp="h2jytyl36eq5">
                      Regulator
                    </h4>
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-13110" data-mp="3amgsvptt5jzt">
                    <div data-id="element-13111" data-mp="3aojul74bp1kd">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13112" data-mp="35nvvdy5xxef2">
                        Brand
                      </label>
                      <div className="relative" data-id="element-13113" data-mp="3kmhdrg7bs864">
                        <select value={regulatorBrand} onChange={e => setRegulatorBrand(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13114" data-mp="3fla93q0hbp3o">
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-13115" data-mp="77ulpeh9pfrj">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-13116" data-mp="g7q15vhuvwxd">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13117" data-mp="ut5b5wjbma8q">
                          <ChevronDownIcon size={16} data-id="element-13118" data-mp="zrycyrp8wct2" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13119" data-mp="qrs2leoufsy4">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13120" data-mp="qra6qj63fgqq">
                        Nominal pressure
                      </label>
                      <div className="relative" data-id="element-13121" data-mp="c12p25nwu1r3">
                        <select value={nominalPressure} onChange={e => setNominalPressure(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13122" data-mp="gunq66ksvbui">
                          <option value="10 psi" className="bg-slate-800 text-white" data-id="element-13123" data-mp="3u88wn9w7tzd4">
                            10 psi
                          </option>
                          <option value="15 psi" className="bg-slate-800 text-white" data-id="element-13124" data-mp="3tbtnyp3o7l12">
                            15 psi
                          </option>
                          <option value="6 psi" className="bg-slate-800 text-white" data-id="element-13125" data-mp="3jfgzc5mzfxd6">
                            6 psi
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13126" data-mp="fygvjh0468v8">
                          <ChevronDownIcon size={16} data-id="element-13127" data-mp="kw9xtyyaqiq8" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13128" data-mp="66b4c9horvh3">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13129" data-mp="b7i8lykmecj0">
                        Vertical position
                      </label>
                      <div className="relative" data-id="element-13130" data-mp="yvrsguy9wq7i">
                        <select value={verticalPosition} onChange={e => setVerticalPosition(e.target.value)} disabled={!regulator} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13131" data-mp="13ob6tu4sbpdd">
                          <option value="Top" className="bg-slate-800 text-white" data-id="element-13132" data-mp="2x8cskipix64p">
                            Top
                          </option>
                          <option value="Bottom" className="bg-slate-800 text-white" data-id="element-13133" data-mp="25gdt3md6bl7b">
                            Bottom
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13134" data-mp="2egaijq7e0o3f">
                          <ChevronDownIcon size={16} data-id="element-13135" data-mp="29fe78rkphznz" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* End Guns and Booster Pump - Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-13136" data-mp="2ij80ku31vhht">
                {/* End Gun 1 */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13137" data-mp="2ngzz46y7a1uz">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-13138" data-mp="2rk665gt4zx2i">
                    End Gun 1
                  </h3>
                  <div className="space-y-5" data-id="element-13139" data-mp="1zt01qo6dvd7f">
                    <div data-id="element-13140" data-mp="1zq42mwisuihe">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13141" data-mp="23tis0dsmob7i">
                        Brand
                      </label>
                      <div className="relative" data-id="element-13142" data-mp="28rbzrb9kxd9w">
                        <select value={endGun1Brand} onChange={e => setEndGun1Brand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13143" data-mp="2djbu8o6vrdz3">
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-13144" data-mp="1djjgwz6aevr6">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-13145" data-mp="18l85wl49af7d">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13146" data-mp="zj8ovst8rb4f">
                          <ChevronDownIcon size={16} data-id="element-13147" data-mp="un0fyx5n0e5p" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13148" data-mp="kukvnt6vbdhn">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13149" data-mp="kvvztu486n9a">
                        Model
                      </label>
                      <div className="relative" data-id="element-13150" data-mp="bug0qiiv27kd">
                        <select value={endGun1Model} onChange={e => setEndGun1Model(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13151" data-mp="bt4l9lg1zbdf">
                          <option value="KPW HEAD-UP" className="bg-slate-800 text-white" data-id="element-13152" data-mp="2ay4qhvtak9o1">
                            KPW HEAD-UP
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13153" data-mp="225udy7uxbs3m">
                          <ChevronDownIcon size={16} data-id="element-13154" data-mp="1n8acvc15eh2q" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13155" data-mp="1e96qnz6oplk0">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13156" data-mp="1e9yozhro1xdz">
                        Control
                      </label>
                      <div className="relative" data-id="element-13157" data-mp="1j9uszh0d5otj">
                        <select value={endGun1Control} onChange={e => setEndGun1Control(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13158" data-mp="38nznu2n7uke2">
                          <option value="Endgun Control wit..." className="bg-slate-800 text-white" data-id="element-13159" data-mp="3irxukysxpssj">
                            Endgun Control wit...
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13160" data-mp="3rq05c62gzzt0">
                          <ChevronDownIcon size={16} data-id="element-13161" data-mp="acwaty3xsl3c" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* End Gun 2 */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13162" data-mp="fa66hvhx5szm">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-13163" data-mp="2l3w4o9hg66bk">
                    End Gun 2
                  </h3>
                  <div className="space-y-5" data-id="element-13164" data-mp="k151kou5qhuu">
                    <div data-id="element-13165" data-mp="k26mrm4w0kqa">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13166" data-mp="k2zr6ggdiy59">
                        Brand
                      </label>
                      <div className="relative" data-id="element-13167" data-mp="tyt789tax9rs">
                        <select value={endGun2Brand} onChange={e => setEndGun2Brand(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13168" data-mp="u15m2e3rj40m">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-13169" data-mp="17wr8cdim39pc">
                            Select brand
                          </option>
                          <option value="Komet" className="bg-slate-800 text-white" data-id="element-13170" data-mp="1hnlyhsyzatg2">
                            Komet
                          </option>
                          <option value="Nelson" className="bg-slate-800 text-white" data-id="element-13171" data-mp="1qpbnyt9avib7">
                            Nelson
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13172" data-mp="3b59tulzzfm66">
                          <ChevronDownIcon size={16} data-id="element-13173" data-mp="326fwuuiqots3" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13174" data-mp="2x8wjrxxs2n1q">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13175" data-mp="2x9pl2jdywkch">
                        Model
                      </label>
                      <div className="relative" data-id="element-13176" data-mp="3pw0hriyge140">
                        <select value={endGun2Model} onChange={e => setEndGun2Model(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13177" data-mp="3pyd92mq3pntq">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-13178" data-mp="hijtca3lggdq">
                            Select model
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13179" data-mp="3h68p7chi7kbc">
                          <ChevronDownIcon size={16} data-id="element-13180" data-mp="386vfcipgwtq3" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-13181" data-mp="3c7p0537m5ser">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13182" data-mp="3c903j1x5cy5c">
                        Control
                      </label>
                      <div className="relative" data-id="element-13183" data-mp="8ri2pefskref">
                        <select value={endGun2Control} onChange={e => setEndGun2Control(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13184" data-mp="8qyskjwdv7ay">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-13185" data-mp="2fgwkg6ole2di">
                            Select control
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13186" data-mp="2bypdt497oxv6">
                          <ChevronDownIcon size={16} data-id="element-13187" data-mp="2811dflor2cvw" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Booster Pump */}
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13188" data-mp="1t9j1q02xb4ic">
                  <h3 className="text-white font-semibold text-base mb-4 pb-3 border-b border-white/10" data-id="element-13189" data-mp="1oc0ohxhvi34j">
                    Booster Pump
                  </h3>
                  <div className="space-y-5" data-id="element-13190" data-mp="1y95cm2va48nz">
                    <div data-id="element-13191" data-mp="1y7avkzn4p0p6">
                      <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13192" data-mp="1y72e0ug2977n">
                        Power
                      </label>
                      <div className="relative" data-id="element-13193" data-mp="15j6n66d6a9is">
                        <select value={boosterPumpPower} onChange={e => setBoosterPumpPower(e.target.value)} className="w-full appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" data-id="element-13194" data-mp="15idlhjt2ma7i">
                          <option value="" className="bg-slate-800 text-blue-300" data-id="element-13195" data-mp="3l0p51el53xq0">
                            Select power
                          </option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-blue-300" data-id="element-13196" data-mp="mgpaf9qf3s3e">
                          <ChevronDownIcon size={16} data-id="element-13197" data-mp="ckchie2p5i9p" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button for Design Preferences */}
              <div className="flex items-center justify-end pt-2" data-id="element-13198" data-mp="2xcfl6i7hlk1i">
                <button onClick={handleSaveDesignPreferences} disabled={savingTab === 'design'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-13199" data-mp="2t8g8kapbv5df">
                  {savingTab === 'design' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-13200" data-mp="1hq2dahieea5q" />
                      Saving...
                    </> : savedTab === 'design' ? <>
                      <CheckCircleIcon size={16} data-id="element-13201" data-mp="1rk2ql6nu2lpj" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-13202" data-mp="1wivzels6dojd" />
                      Save Design Preferences
                    </>}
                </button>
              </div>
            </div>}

          {/* Quote Tab */}
          {activeTab === 'quote' && <div className="space-y-6" data-id="element-13203" data-mp="65583i02g009">
              <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-xl shadow-lg border border-white/10 p-6" data-id="element-13204" data-mp="666m9vcdp2m7">
                <h3 className="text-white font-semibold text-base mb-2 pb-3 border-b border-white/10" data-id="element-13205" data-mp="1eem5piodxfow">
                  Dealer Mark-up
                </h3>
                <p className="text-blue-300/70 text-sm mb-6" data-id="element-13206" data-mp="19h3ypxtxhajj">
                  This is the default margin percentage applied to all quotes
                  generated for your customers. When a new quote is created,
                  this mark-up will be automatically added on top of the base
                  price to calculate the final customer price. You can also
                  configure a different mark-up for each individual customer,
                  and you can still adjust it on individual quotes if needed.
                </p>
                <div data-id="element-13207" data-mp="224gm2a7wxmdw">
                  <label className="block text-sm font-medium text-blue-300 mb-1.5" data-id="element-13208" data-mp="223yiesix85vy">
                    Default mark-up <span className="text-red-400" data-id="element-13209" data-mp="2g51597hqpk7x">*</span>
                  </label>
                  <div className="relative w-48" data-id="element-13210" data-mp="2b5epaqaj1pvf">
                    <input type="number" step="0.01" value={dealerMarkup} onChange={e => setDealerMarkup(e.target.value)} className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-2.5 pr-10 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" placeholder="25.00" data-id="element-13211" data-mp="2b6pr4kkewnjw" />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none text-blue-300" data-id="element-13212" data-mp="2pqslsuj5qzbr">
                      %
                    </div>
                  </div>
                </div>
              </div>

              {/* Save Button for Quote */}
              <div className="flex items-center justify-end pt-2" data-id="element-13213" data-mp="2229ccbodggi3">
                <button onClick={handleSaveQuote} disabled={savingTab === 'quote'} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white rounded-lg px-5 py-2.5 text-sm font-medium transition-colors shadow-lg shadow-blue-900/20" data-id="element-13214" data-mp="21zwlta4jsxtf">
                  {savingTab === 'quote' ? <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-13215" data-mp="1pzq5ixqp0l51" />
                      Saving...
                    </> : savedTab === 'quote' ? <>
                      <CheckCircleIcon size={16} data-id="element-13216" data-mp="1g9wubxugq81b" />
                      Saved!
                    </> : <>
                      <SaveIcon size={16} data-id="element-13217" data-mp="1bb3liiq4f57h" />
                      Save Quote Settings
                    </>}
                </button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}